<script setup>
  import Icon from '@/components/icons/Icon.vue';
  import { useToastHost } from '@/composables/useToaster';

  const { toasts, clear, isActiveHost } = useToastHost();

  const typeIcons = {
    success: 'checkmark',
    warning: 'exclamation-circle',
    favorite: 'star-solid',
  };
</script>

<template>
  <Teleport to="body">
    <div v-if="isActiveHost" class="jwx-v-toasts" role="status" aria-live="polite" aria-atomic="true">
      <TransitionGroup name="jwx-toast">
        <div v-for="toast in toasts" :key="toast.id" class="jwx-v-toast" :data-type="toast.type">
          <div class="jwx-v-toast__icon">
            <Icon v-if="typeIcons[toast.type]" :name="typeIcons[toast.type]" />
          </div>
          <div class="jwx-v-toast__body">
            <p v-if="toast.title" class="jwx-v-toast__title">{{ toast.title }}</p>
            <!-- contentHtml is the explicit trusted-markup channel; templates are
                 developer-authored and dynamic values pass through escapeHtml().
                 Never pipe raw user input through contentHtml. -->
            <p v-if="toast.contentHtml" class="jwx-v-toast__content" v-html="toast.contentHtml"></p>
            <p v-else-if="toast.content" class="jwx-v-toast__content">{{ toast.content }}</p>
          </div>
          <button class="jwx-v-toast__closer" type="button" @click="clear(toast.id)">
            <Icon name="close-x" />
            <span class="sr-only">Dismiss this notification.</span>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
  .jwx-v-toasts {
    --jwx-toast-spacing: .75rem;
    --jwx-toast-surface: var(--jwx-surface, hsl(0, 0%, 100%));
    --jwx-toast-text-color: var(--jwx-text-color, hsl(0, 0%, 35%));
    --jwx-toast-icon-color: hsl(0, 0%, 100%);
    --jwx-toast-icon-background: hsl(0, 0%, 90%);
    --jwx-toast-border-radius: 8px;

    position: fixed;
    inset: auto 0 0 0;
    display: grid;
    grid-auto-flow: row;
    grid-auto-rows: min-content;
    justify-items: center;
    gap: .5rem;
    padding: var(--jwx-toast-spacing);
    font-family: var(--font-family, inherit);
    pointer-events: none;
    z-index: 1000;
  }

  .jwx-v-toast {
    display: grid;
    grid-template-columns: min-content 1fr min-content;
    align-items: center;
    gap: var(--jwx-toast-spacing);
    padding: var(--jwx-toast-spacing);
    width: calc(100% - 3rem);
    max-height: 100vh; // the collapse keyframe animates this to 0
    background-color: var(--jwx-toast-surface);
    border-radius: var(--jwx-toast-border-radius);
    color: var(--jwx-toast-text-color);
    pointer-events: all;

    &[data-type="success"],
    &[data-type="favorite"] {
      --jwx-toast-icon-background: var(--color-success, hsl(102, 92%, 28%));
    }

    &[data-type="warning"] {
      --jwx-toast-icon-background: var(--color-warning-light, hsl(54, 100%, 65%));
    }

    @media screen and (min-width: 450px) {
      width: 20rem;
    }
  }

  .jwx-v-toast__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background-color: var(--jwx-toast-icon-background);
    border-radius: 100%;
    border: 1px solid hsla(360, 100%, 100%, 0.2);
    color: var(--jwx-toast-icon-color);

    > svg {
      width: 1.5rem;
      fill: currentColor;
    }
  }

  .jwx-v-toast__body > * {
    margin: 0;
  }

  .jwx-v-toast__title {
    font-size: 1rem;
    font-weight: 700;
  }

  .jwx-v-toast__content {
    font-size: .875rem;
    font-weight: 400;
    line-height: 1.3;

    // Scoped CSS can't reach v-html output, so contentHtml descendants need :deep().
    :deep(i) {
      font-style: italic;
    }

    :deep(strong) {
      font-weight: 600;
    }
  }

  .jwx-v-toast__closer {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    appearance: none;
    background-color: transparent;
    border: none;
    border-radius: var(--border-radius, 5px);
    opacity: .75;
    cursor: pointer;
    color: var(--jwx-toast-text-color);
    transition: all 220ms ease-in-out;

    &:not(:focus-visible) {
      outline: none;
    }

    &:hover,
    &:focus {
      opacity: 1;
      transform: scale(1.1);
    }

    &:focus-visible {
      box-shadow: 0 0 0 2px hsla(360, 100%, 100%, 0.5);
    }

    > svg {
      width: 1rem;
      fill: currentColor;
    }
  }

  // Enter/leave — the resting state is the default, so no `is-visible` class and no
  // @after-enter hook. TransitionGroup removes the node when `collapse` ends.
  .jwx-toast-enter-active {
    animation: jwx-toast-slide-in 400ms ease-in-out;
  }

  .jwx-toast-leave-active {
    animation: jwx-toast-collapse 200ms ease-in-out forwards;

    > * {
      opacity: 0;
      transition: opacity 100ms ease-in-out;
    }
  }

  .jwx-toast-move {
    transition: transform 200ms ease-in-out;
  }

  @keyframes jwx-toast-slide-in {
    from {
      transform: translate3d(0, calc(100% + var(--jwx-toast-spacing)), 0);
    }
  }

  @keyframes jwx-toast-collapse {
    to {
      max-height: 0;
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .jwx-toast-enter-active,
    .jwx-toast-leave-active,
    .jwx-toast-move {
      animation: none;
      transition: none;
    }
  }
</style>
